import React, { useState, useEffect, useRef, useCallback, useMemo, useLayoutEffect } from 'react';
import { Send, MessageSquare, ArrowDown, Loader2 } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { useAuth } from '../../context/AuthContext';
import {
  PAGE_WIDE, Workbench, WorkbenchList, WorkbenchDetail, SearchInput, EmptyState, ErrorState,
  PersonAvatar, fmtAgo, fmtDate, fmtDayMonth, fmtTime, humanise, num,
} from '../../components/ui';

// Messages: an inbox of people, newest conversation first, and the open conversation beside it.
// The open thread refreshes itself every 12 seconds while this tab is visible (it used to load only
// when you picked someone or sent a message, so a worker's reply never appeared), and the inbox
// every 30 seconds so unread counts stay current.

const THREAD_POLL_MS = 12000;
const INBOX_POLL_MS = 30000;
const GROUP_GAP_MS = 5 * 60 * 1000;

const ROLE_LABEL = {
  admin: 'Admin', super_admin: 'Admin', office_user: 'Office user', carer: 'Carer',
  coordinator: 'Coordinator', hr: 'HR', office_support: 'Office support', manager: 'Manager',
};
const roleLabel = (r) => ROLE_LABEL[r] || humanise(r) || '';
const fullName = (p) => `${(p && p.first_name) || ''} ${(p && p.last_name) || ''}`.trim() || 'Unknown';

// created_at is a bare TIMESTAMP that the server reads ~10h early, so prefer the epoch the API
// now sends alongside it; fall back to created_at against an older server.
const instantOfMsg = (m) => (m && m.created_epoch != null ? Number(m.created_epoch) * 1000 : new Date(m && m.created_at).getTime());
const lastInstant = (c) => (c.last_message_epoch != null ? Number(c.last_message_epoch) * 1000
  : c.last_message_at ? new Date(c.last_message_at).getTime() : null);

const startOfDay = (ts) => { const d = new Date(ts); d.setHours(0, 0, 0, 0); return d.getTime(); };
function dayLabel(ts) {
  const diff = Math.round((startOfDay(Date.now()) - startOfDay(ts)) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  const d = new Date(ts);
  return d.getFullYear() === new Date().getFullYear() ? fmtDayMonth(d) : fmtDate(d);
}
const clockOf = (ts) => { const d = new Date(ts); return fmtTime(`${d.getHours()}:${d.getMinutes()}`); };

// Day dividers, then runs of consecutive messages from one person within five minutes, each run
// carrying one timestamp.
function groupThread(thread, myId) {
  const days = [];
  thread.forEach((m) => {
    const ts = instantOfMsg(m);
    const dayKey = startOfDay(ts);
    let day = days[days.length - 1];
    if (!day || day.key !== dayKey) { day = { key: dayKey, label: dayLabel(ts), groups: [] }; days.push(day); }
    const mine = m.sender_id === myId;
    const g = day.groups[day.groups.length - 1];
    if (g && g.senderId === m.sender_id && ts - g.lastTs <= GROUP_GAP_MS) {
      g.items.push(m); g.lastTs = ts;
    } else {
      day.groups.push({ key: m.id, senderId: m.sender_id, senderName: m.sender_name, mine, items: [m], lastTs: ts });
    }
  });
  return days;
}

const threadSig = (t) => `${t.length}:${t.length ? t[t.length - 1].id : ''}`;

function ContactSkeleton() {
  return (
    <div className="p-4 space-y-4" aria-busy="true" aria-label="Loading conversations">
      {Array.from({ length: 7 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="skeleton w-10 h-10 rounded-full shrink-0" />
          <div className="flex-1 space-y-1.5"><div className="skeleton h-3.5 w-2/3" /><div className="skeleton h-3 w-5/6" /></div>
        </div>
      ))}
    </div>
  );
}

function ContactRow({ c, active, onOpen }) {
  const unread = Number(c.unread_count) || 0;
  const ts = lastInstant(c);
  const role = roleLabel(c.role);
  const preview = c.last_message ? `${c.last_message_mine ? 'You: ' : ''}${c.last_message}` : 'No messages yet';
  return (
    <button type="button" onClick={() => onOpen(c)} aria-current={active ? 'true' : undefined}
      className={clsx('w-full flex items-start gap-3 px-4 py-3 text-left border-b border-slate-100 transition-colors duration-150',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-400',
        active ? 'bg-primary-50' : 'hover:bg-slate-50')}>
      <PersonAvatar id={c.id} firstName={c.first_name} lastName={c.last_name} photo={c.profile_photo} size="md" />
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-2">
          <span className={clsx('truncate text-[13.5px]', unread ? 'font-semibold text-slate-900' : 'font-medium text-slate-800')}>{fullName(c)}</span>
          {role && <span className="shrink-0 text-[12px] text-slate-500">{role}</span>}
          {ts && <span className="ml-auto shrink-0 pl-1 text-[11.5px] tabular-nums text-slate-500">{fmtAgo(new Date(ts))}</span>}
        </span>
        <span className="flex items-center gap-2 mt-0.5">
          <span className={clsx('truncate text-[12.5px]', unread ? 'text-slate-800' : 'text-slate-500')}>{preview}</span>
          {unread > 0 && (
            <span className="ml-auto shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-primary-600 text-white text-[11px] font-semibold tabular-nums inline-flex items-center justify-center">
              {num(unread)}<span className="sr-only"> unread</span>
            </span>
          )}
        </span>
      </span>
    </button>
  );
}

export default function Messages() {
  const { user } = useAuth();
  const myId = user?.id;
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [contactsError, setContactsError] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const [atBottom, setAtBottom] = useState(true);
  const [newPill, setNewPill] = useState(false);

  const scrollRef = useRef(null);
  const bottomRef = useRef(null);
  const composerRef = useRef(null);
  const inputRef = useRef(null);
  const selectedRef = useRef(null);
  const threadRef = useRef([]);
  const atBottomRef = useRef(true);
  const pendingScroll = useRef(null); // 'instant' | 'smooth' | null

  useEffect(() => { selectedRef.current = selectedId; }, [selectedId]);
  useEffect(() => { threadRef.current = thread; }, [thread]);
  useEffect(() => { atBottomRef.current = atBottom; if (atBottom) setNewPill(false); }, [atBottom]);

  // ── Inbox ──────────────────────────────────────────────────────────────────
  const loadContacts = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) { setContactsLoading(true); setContactsError(false); }
    try {
      const r = await api.get('/messages/contacts');
      setContacts(Array.isArray(r.data) ? r.data : []);
      setContactsError(false);
    } catch {
      if (!quiet) setContactsError(true);
    } finally {
      if (!quiet) setContactsLoading(false);
    }
  }, []);

  useEffect(() => { loadContacts(); }, [loadContacts]);

  // ── Thread ─────────────────────────────────────────────────────────────────
  const loadThread = useCallback(async (id, { quiet = false } = {}) => {
    if (!id) return;
    if (!quiet) { setThreadLoading(true); setThreadError(false); }
    try {
      const r = await api.get(`/messages/thread/${id}`);
      if (selectedRef.current !== id) return; // moved to another conversation meanwhile
      const next = Array.isArray(r.data) ? r.data : [];
      const prev = threadRef.current;
      if (quiet && threadSig(prev) === threadSig(next)) return;
      const prevIds = new Set(prev.map((m) => m.id));
      const incoming = quiet && next.some((m) => !prevIds.has(m.id) && m.sender_id !== myId);
      if (!quiet) pendingScroll.current = 'instant';
      else if (atBottomRef.current) pendingScroll.current = 'smooth';
      else if (incoming) setNewPill(true);
      setThread(next);
      setThreadError(false);
      // Opening a thread marks it read on the server; mirror that in the inbox straight away.
      setContacts((cs) => cs.map((c) => (c.id === id && c.unread_count ? { ...c, unread_count: 0 } : c)));
    } catch {
      if (!quiet && selectedRef.current === id) setThreadError(true);
    } finally {
      if (!quiet && selectedRef.current === id) setThreadLoading(false);
    }
  }, [myId]);

  useEffect(() => {
    setThread([]);
    setNewPill(false);
    setAtBottom(true);
    if (selectedId) loadThread(selectedId);
  }, [selectedId, loadThread]);

  // Keep the open conversation (and the inbox) live while the tab is visible.
  useEffect(() => {
    const visible = () => document.visibilityState === 'visible';
    const threadTimer = setInterval(() => { if (visible() && selectedRef.current) loadThread(selectedRef.current, { quiet: true }); }, THREAD_POLL_MS);
    const inboxTimer = setInterval(() => { if (visible()) loadContacts({ quiet: true }); }, INBOX_POLL_MS);
    const onVis = () => {
      if (!visible()) return;
      if (selectedRef.current) loadThread(selectedRef.current, { quiet: true });
      loadContacts({ quiet: true });
    };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(threadTimer); clearInterval(inboxTimer); document.removeEventListener('visibilitychange', onVis); };
  }, [loadThread, loadContacts]);

  // ── Scrolling ──────────────────────────────────────────────────────────────
  const scrollToEnd = useCallback((smooth) => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' }); // wide: the thread scrolls in its pane
    // Narrow screens: the page scrolls, so bring the composer (and the newest message above it) into view.
    if (window.matchMedia && window.matchMedia('(max-width: 1023px)').matches && composerRef.current) {
      composerRef.current.scrollIntoView({ block: 'end', behavior: smooth ? 'smooth' : 'auto' });
    }
  }, []);

  useLayoutEffect(() => {
    if (!pendingScroll.current) return;
    const mode = pendingScroll.current;
    pendingScroll.current = null;
    scrollToEnd(mode === 'smooth');
  }, [thread, scrollToEnd]);

  // Is the newest message on screen? Decides between following new messages and showing the pill.
  useEffect(() => {
    const el = bottomRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(([entry]) => setAtBottom(entry.isIntersecting), { rootMargin: '0px 0px -64px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [selectedId, threadLoading]);

  // ── Send ───────────────────────────────────────────────────────────────────
  const sendMessage = async (e) => {
    if (e) e.preventDefault();
    const text = message.trim();
    if (!text || !selectedId) return;
    setSending(true);
    try {
      await api.post('/messages', { recipientId: selectedId, content: text });
      setMessage('');
      atBottomRef.current = true;
      await loadThread(selectedId, { quiet: true });
      pendingScroll.current = 'smooth';
      setThread((t) => [...t]); // make sure the scroll-to-end runs even when the poll saw it first
      loadContacts({ quiet: true });
    } catch {
      toast.error('Message not sent. Check your connection, then try again.');
    } finally {
      setSending(false);
      if (inputRef.current) inputRef.current.focus();
    }
  };

  // ── Derived ────────────────────────────────────────────────────────────────
  const sorted = useMemo(() => {
    const withTs = contacts.map((c, i) => ({ c, i, ts: lastInstant(c) }));
    withTs.sort((a, b) => {
      if (a.ts != null && b.ts != null) return b.ts - a.ts;
      if (a.ts != null) return -1;
      if (b.ts != null) return 1;
      return a.i - b.i; // API order (alphabetical) for people you haven't messaged yet
    });
    return withTs.map((x) => x.c);
  }, [contacts]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return s ? sorted.filter((c) => fullName(c).toLowerCase().includes(s)) : sorted;
  }, [sorted, search]);

  const selected = useMemo(() => contacts.find((c) => c.id === selectedId) || null, [contacts, selectedId]);
  const days = useMemo(() => groupThread(thread, myId), [thread, myId]);
  const totalUnread = useMemo(() => contacts.reduce((n, c) => n + (Number(c.unread_count) || 0), 0), [contacts]);

  const openContact = (c) => { if (c.id !== selectedId) { setMessage(''); setSelectedId(c.id); } };

  return (
    <div className={PAGE_WIDE}>
      <Workbench detailOpen={!!selectedId} onCloseDetail={() => setSelectedId(null)} offset="7rem">
        <WorkbenchList width="sm" label="Conversations">
          <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3 space-y-2">
            <SearchInput value={search} onChange={setSearch} placeholder="Search people" />
            <p className="text-[12px] text-slate-500 tabular-nums" aria-live="polite">
              {contactsLoading ? 'Loading…' : totalUnread > 0 ? `${num(totalUnread)} unread` : 'All caught up'}
            </p>
          </div>
          {contactsLoading ? <ContactSkeleton />
            : contactsError ? <ErrorState message="We couldn't load your conversations. Check your connection, then try again." onRetry={() => loadContacts()} />
            : filtered.length === 0 ? (
              contacts.length === 0
                ? <EmptyState icon={MessageSquare} title="No one to message yet" hint="Active staff you can message appear here." />
                : <EmptyState icon={MessageSquare} title="No one matches that name" action={<button type="button" className="btn-secondary btn-sm" onClick={() => setSearch('')}>Clear search</button>} />
            ) : (
              <div>
                {filtered.map((c) => <ContactRow key={c.id} c={c} active={c.id === selectedId} onOpen={openContact} />)}
              </div>
            )}
        </WorkbenchList>

        <WorkbenchDetail label="Conversation" backLabel="Back to conversations" bodyClassName="flex flex-col"
          placeholder={<EmptyState icon={MessageSquare} title="Choose a conversation" hint="Pick someone on the left to read and reply to their messages." />}>
          {selected || selectedId ? (
            <>
              <div className="flex items-center gap-3 px-5 py-3.5 border-b border-slate-100 shrink-0">
                <PersonAvatar id={selectedId} firstName={selected?.first_name} lastName={selected?.last_name} photo={selected?.profile_photo} size="md" />
                <div className="min-w-0">
                  <h2 className="text-[15px] font-semibold text-slate-900 truncate">{fullName(selected)}</h2>
                  {selected?.role && <p className="text-[12.5px] text-slate-500">{roleLabel(selected.role)}</p>}
                </div>
              </div>

              <div className="relative flex-1 min-h-0 flex flex-col">
                <div ref={scrollRef} role="log" aria-live="polite" aria-label={`Messages with ${fullName(selected)}`}
                  className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 sm:px-5 pb-4">
                  {threadLoading ? (
                    <div className="py-6 space-y-4" aria-busy="true" aria-label="Loading messages">
                      {[60, 40, 70, 35].map((w, i) => (
                        <div key={i} className={clsx('flex', i % 2 ? 'justify-end' : 'justify-start')}>
                          <div className="skeleton h-9 rounded-2xl" style={{ width: `${w}%` }} />
                        </div>
                      ))}
                    </div>
                  ) : threadError ? (
                    <ErrorState message="We couldn't load this conversation. Check your connection, then try again." onRetry={() => loadThread(selectedId)} />
                  ) : thread.length === 0 ? (
                    <p className="text-center text-[13px] text-slate-500 py-10">No messages yet</p>
                  ) : (
                    days.map((day) => (
                      <section key={day.key} aria-label={day.label}>
                        <div className="flex items-center gap-3 pt-5 pb-3" role="separator" aria-label={day.label}>
                          <span className="h-px flex-1 bg-slate-100" />
                          <span className="text-[11.5px] font-medium text-slate-500">{day.label}</span>
                          <span className="h-px flex-1 bg-slate-100" />
                        </div>
                        {day.groups.map((g) => (
                          <div key={g.key} className={clsx('flex gap-2 mb-3', g.mine ? 'flex-row-reverse' : 'flex-row')}>
                            {!g.mine && (
                              <PersonAvatar id={g.senderId} name={g.senderName} size="xs" className="mt-auto mb-5" />
                            )}
                            <div className={clsx('flex flex-col gap-1 max-w-[85%] sm:max-w-[70%]', g.mine ? 'items-end' : 'items-start')}>
                              {g.items.map((m) => (
                                <div key={m.id}
                                  className={clsx('px-3.5 py-2 rounded-2xl text-[13.5px] leading-relaxed whitespace-pre-wrap break-words',
                                    g.mine ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-900')}>
                                  {m.content}
                                </div>
                              ))}
                              <span className="text-[11px] text-slate-500 px-1 tabular-nums">
                                {g.mine ? 'You' : (g.senderName || fullName(selected))} · {clockOf(g.lastTs)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </section>
                    ))
                  )}
                  <div ref={bottomRef} aria-hidden="true" className="h-px" />
                </div>

                {newPill && (
                  <button type="button" onClick={() => { setNewPill(false); scrollToEnd(true); }}
                    className="fixed lg:absolute bottom-24 lg:bottom-3 left-1/2 -translate-x-1/2 z-20 inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-slate-900 text-white text-[12px] font-semibold shadow-card-deep hover:bg-slate-800 transition-colors duration-150">
                    <ArrowDown size={13} /> New messages
                  </button>
                )}
              </div>

              <form ref={composerRef} onSubmit={sendMessage}
                className="flex items-center gap-2 p-3 sm:p-4 border-t border-slate-100 bg-white shrink-0 scroll-mb-24">
                <label htmlFor="message-composer" className="sr-only">Message {fullName(selected)}</label>
                <input
                  id="message-composer" ref={inputRef} className="input-field flex-1" autoComplete="off"
                  placeholder={selected?.first_name ? `Message ${selected.first_name}…` : 'Write a message…'}
                  value={message} onChange={(e) => setMessage(e.target.value)} maxLength={4000}
                />
                <button type="submit" disabled={!message.trim() || sending} aria-label="Send message"
                  className="btn-primary !px-0 w-10 h-10 shrink-0">
                  {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                </button>
              </form>
            </>
          ) : null}
        </WorkbenchDetail>
      </Workbench>
    </div>
  );
}
